/**
 * The 9-slice of the direction flag on a parked bus's roof.
 *
 * `Arrow (1)` on every ConveyorBox prefab carries a SpriteRenderer with
 * `m_DrawMode: 1` (Sliced), `m_Sprite` pointing at `Sprite/Arrow_0.asset` and
 * an `m_Size` that differs per bus size. Slicing is what lets one 512x512
 * picture serve a 3.5-unit flag and a 7.7-unit one without the head or the
 * rounded foot changing shape: only the straight shaft between them stretches.
 *
 * The sprite's `m_Border` has zero on left and right, so there is nothing to
 * slice across the width — three bands up the length is the whole of it.
 */

import { ARROW_BORDER_FOOT, ARROW_BORDER_HEAD, ARROW_SPRITE_UNITS } from '../game/tuning'

export interface SlicedQuad {
  /** Interleaved xyz, 4 rows of 2 vertices, bottom row first. */
  positions: number[]
  /** Interleaved uv, matching `positions`. */
  uvs: number[]
  /** Two triangles per band, three bands. */
  indices: number[]
  /** The band heights actually used, bottom-up: foot, shaft, head. */
  bands: [number, number, number]
}

/**
 * Lay the sprite out at `width` x `length`, centred on the origin in XY, with
 * the head at +Y.
 *
 * ❓ PLACEHOLDER, and only for the Small bus. Its `m_Size` length is 3.5 while
 * the two fixed bands already come to 4.17, so the sliced middle would have to
 * be negative. What Unity's native sliced-sprite mesh does in that case is not
 * in the decomp — it is engine code, not game code — so the two candidates are
 * both guesses: shrink the borders to fit (taken here, because it can never
 * produce inverted geometry), or keep them at full size and let the sprite
 * overflow its own `m_Size`. Settling it needs a frame of a Small bus's roof
 * from `reference/gameplay.mp4` measured against a Medium one in the same
 * frame; the ratio is 0.84 under this branch and 1.19 under the other. The
 * 300-pixel level-4 screenshot is not enough — see the note in
 * GAMEPLAY-CHECKLIST §10.
 */
export function sliceArrow(width: number, length: number): SlicedQuad {
  const fixed = ARROW_BORDER_FOOT + ARROW_BORDER_HEAD
  // Both borders shrink by the same factor when they cannot fit, which keeps
  // the head-to-foot proportion the sprite was drawn with.
  const squeeze = length < fixed ? length / fixed : 1
  const foot = ARROW_BORDER_FOOT * squeeze
  const head = ARROW_BORDER_HEAD * squeeze
  const shaft = Math.max(0, length - foot - head)

  const halfW = width / 2
  const halfL = length / 2
  const ys = [-halfL, -halfL + foot, -halfL + foot + shaft, halfL]
  // The v cuts never move: they are where the picture is cut, not where the
  // geometry is. A squeezed border still shows the whole border pixels.
  const vs = [0, ARROW_BORDER_FOOT / ARROW_SPRITE_UNITS, 1 - ARROW_BORDER_HEAD / ARROW_SPRITE_UNITS, 1]

  const positions: number[] = []
  const uvs: number[] = []
  for (let row = 0; row < 4; row++) {
    positions.push(-halfW, ys[row], 0, halfW, ys[row], 0)
    uvs.push(0, vs[row], 1, vs[row])
  }

  const indices: number[] = []
  for (let band = 0; band < 3; band++) {
    const a = band * 2
    indices.push(a, a + 1, a + 2, a + 1, a + 3, a + 2)
  }

  return { positions, uvs, indices, bands: [foot, shaft, head] }
}
